import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { MediaService } from '../../../Services/Axios/Api';
import {
    isImageFileObject,
    isVideoFileObject,
    isPdfFileObject,
    isAllowedMediaFile,
} from './mediaFileUtils';

const UploadMediaDialog = ({ open, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [altText, setAltText] = useState('');
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const MAX_FILE_SIZE_MB = 50;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setError(`File size must be less than ${MAX_FILE_SIZE_MB} MB`);
            setFile(null);
            setPreview(null);
            return;
        }

        if (!isAllowedMediaFile(selectedFile)) {
            setError('Please select an image, video, or PDF file');
            setFile(null);
            setPreview(null);
            return;
        }

        setFile(selectedFile);
        setError('');

        if (isPdfFileObject(selectedFile)) {
            setPreview('pdf');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(selectedFile);
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file to upload');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('altText', altText);

            await MediaService.createMedia(formData);
            onSuccess();
            handleClose();
        } catch (uploadError) {
            setError('Failed to upload file. Please try again.');
            console.error('Upload error:', uploadError);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setAltText('');
        setPreview(null);
        setError('');
        onClose();
    };

    const renderPreview = () => {
        if (!preview || !file) return null;

        if (isPdfFileObject(file) || preview === 'pdf') {
            return (
                <div className="media-pdf-preview text-center mb-3">
                    <FontAwesomeIcon icon={faFilePdf} className="media-pdf-preview-icon" />
                    <p className="mb-0 mt-2 text-muted small text-truncate px-2">{file.name}</p>
                </div>
            );
        }

        if (isImageFileObject(file)) {
            return (
                <div className="text-center mb-3">
                    <img
                        src={preview}
                        alt="Preview"
                        className="img-fluid"
                        style={{ maxHeight: '200px' }}
                    />
                </div>
            );
        }

        if (isVideoFileObject(file)) {
            return (
                <div className="text-center mb-3">
                    <video
                        controls
                        src={preview}
                        className="img-fluid"
                        style={{ maxHeight: '200px' }}
                    />
                </div>
            );
        }

        return null;
    };

    return (
        <Modal show={open} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Upload Media</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Select file (image, video, or PDF — max {MAX_FILE_SIZE_MB} MB)</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*,video/*,.pdf,application/pdf"
                            onChange={handleFileChange}
                            className="mb-3"
                        />

                        {renderPreview()}

                        <Form.Group className="mb-3">
                            <Form.Label>Alt Text</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter file description"
                                value={altText}
                                onChange={(e) => setAltText(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                A brief description for accessibility and search
                            </Form.Text>
                        </Form.Group>

                        {error && (
                            <div className="text-danger mb-3">
                                {error}
                            </div>
                        )}
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={!file || loading}
                >
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Uploading...
                        </>
                    ) : (
                        'Upload'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UploadMediaDialog;
